import { CommonModule, ViewportScroller } from '@angular/common';
import { ChangeDetectorRef, Component, ElementRef, HostListener, SecurityContext, ViewChild } from '@angular/core';
import { FormArray, FormBuilder, FormControl, FormGroup, ReactiveFormsModule, Validators } from '@angular/forms';
import { MatExpansionModule, MatExpansionPanel } from '@angular/material/expansion';
import { TranslateModule } from '@ngx-translate/core';
import { InViewportDirective } from '../../../directives/in-viewport.directive';
import { TicketInfo } from '../interfaces/ticket-info';
import { Image } from '../../systems/components/interfaces/image';
import { Connect } from '../../../classes/connect';
import { ApiResponse } from '../../../interfaces/api-response';
import { ConnectServerService } from '../../../services/connect-server.service';
import { MatDialog } from '@angular/material/dialog';
import { ImageViewerComponent } from '../pop-up/image-viewer/image-viewer.component';
import { PdfViewerComponent } from '../pop-up/pdf-viewer/pdf-viewer.component';
import { DomSanitizer, SafeHtml } from '@angular/platform-browser';
import { SystemInfoPopupComponent } from '../../systems/system-info-popup/system-info-popup.component';
import { ActivatedRoute, Router } from '@angular/router';
import { TicketMessage } from '../interfaces/ticket-message';
import { QuillModule } from 'ngx-quill';
import { PopupDialogService } from '../../../services/popup-dialog.service';

@Component({
  selector: 'app-ticket-modify',
  standalone: true,
  imports: [
    CommonModule,
    ReactiveFormsModule,
    MatExpansionModule,
    TranslateModule,
    InViewportDirective,
    QuillModule
  ],
  templateUrl: './ticket-modify.component.html',
  styleUrl: './ticket-modify.component.scss'
})
export class TicketModifyComponent {

  @ViewChild('bottomAnchor') bottomAnchor!: ElementRef;
  @ViewChild('panel') panelComponent!: MatExpansionPanel;
  isNewMessage: boolean = false;
  ticketInfo: TicketInfo | null = null;
  isSmallScreen: boolean = false;
  maxImages: number = 10;
  fileList: File[] = [];
  imagesList: Image[] = [];
  acceptedExt: string[] = ['jpg', 'png', 'jpeg'];
  imageSpaceLeft: boolean = true;
  systemStatus: { id: number, name: string, color: string } = { id: 0, name: '', color: '' };
  maxFiles: number = 3;
  maxFileSize: number = 50 * 1024 * 1024;
  submitted: boolean = false;
  messagesList: TicketMessage[] = [];
  idticket: number = 0;
  urlServerLaraMedia: string = Connect.urlServerLaraMedia;

  newAttachedFiles: any[] = [];
  newMessageForm = new FormGroup({
    description: new FormControl<string | null>(null, Validators.required),
    date: new FormControl<string | null>(null),
    time: new FormControl<string | null>(null),
  });

  constructor(private fb: FormBuilder, private viewportScroller: ViewportScroller,
    private connectServerService: ConnectServerService, private dialog: MatDialog,
    private sanitizer: DomSanitizer, private route: ActivatedRoute, private router: Router,
    private popupDialogService: PopupDialogService) { }

  ngOnInit(): void {
    this.route.params.subscribe(params => {
      this.idticket = params['id'];
    });
    this.getAllData();
    this.updateWindowDimensions();
  }

  ngAfterViewInit(): void {
    this.scrollToTop();
  }

  @HostListener('window:resize', ['$event'])
  onResize(event: Event): void {
    this.updateWindowDimensions();
  }

  updateWindowDimensions() {
    if (window.innerWidth < 768) {
      this.isSmallScreen = true;
    }
    else {
      this.isSmallScreen = false;
    }
  }

  private scrollToTop(): void {
    if (window.scrollY > 0) {
      window.scrollTo({ top: 0, behavior: 'smooth' });
    }
  }

  goBack() {
    this.router.navigate(['systemOverview', this.ticketInfo?.idsystem]);
  }

  newMessage(): void {
    this.isNewMessage = true;
    this.submitted = false;
    const now = new Date();
    const dateStr = now.toISOString().split('T')[0]; // "YYYY-MM-DD"
    const timeStr = now.toTimeString().split(' ')[0]; // "HH:MM:SS"
    this.newMessageForm.patchValue({
      date: dateStr,
      time: timeStr,
    });
    this.panelComponent.close();
    setTimeout(() => {
      this.bottomAnchor.nativeElement.scrollIntoView({ behavior: 'smooth' });
    }, 100);
  }

  sendMessage() {
    // CHIAMATA AL SERVER E POI NAVIGA CON L'ID RESTITUITO
    const formData = new FormData();
    this.submitted = true;
    if (this.newMessageForm.valid) {

      // Aggiungi i file al formData
      this.fileList.forEach((file, index) => {
        formData.append('attachments[]', file);
      });

      // Aggiungi i valori del form al formData
      Object.keys(this.newMessageForm.controls).forEach(key => {
        const control = this.newMessageForm.get(key);

        if (control instanceof FormArray) {
          // Se il controllo è un FormArray, aggiungi ciascun valore come array JSON
          formData.append(key, JSON.stringify(control.value));
        } else {
          formData.append(key, control?.value);
        }
      });

      // Rimuovi data e ora creazione
      formData.delete('date');
      formData.delete('time');
      formData.append('idticket', this.idticket.toString());

      this.connectServerService.postRequest(Connect.urlServerLaraApi, 'ticket/insertTicketLine', formData)
        .subscribe((val: ApiResponse<any>) => {
          if (val.data) {
            this.popupDialogService.alertElement(val);
            this.getMessages();
            this.newMessageForm.reset();
            this.isNewMessage = false;
            this.newAttachedFiles = [];
            this.imagesList = [];
            this.fileList.splice(0, this.fileList.length)
          }
        })
    }
  }

  viewImage(img: Image) {
    // console.log(img)
    if (img.ext != 'pdf' && this.acceptedExt.includes(img.ext)) {
      const dialogRef = this.dialog.open(ImageViewerComponent, {
        minWidth: '350px',
        panelClass: 'custom-modal-panel',
        backdropClass: 'custom-modal-backdrop',
        maxWidth: '100vw',
        maxHeight: '100vh',
        data: { image: img }
      });
    }
    else if (img.ext == 'pdf') {
      const dialogRef = this.dialog.open(PdfViewerComponent, {
        maxWidth: '700px',
        minWidth: '350px',
        maxHeight: '500px',
        data: { pdf: img }
      });
    }
    else {
      if(img.id > 0) {
        const urlString = this.sanitizer.sanitize(SecurityContext.URL, img.src);
        const newTab = window.open(this.urlServerLaraMedia+urlString!, "_blank");
      }
      else {
        const newTab = window.open(img.src.toString(), "_blank");
      }
    }
  }

  systemInfoPopup() {
    //console.log("IDSYSTEM", this.idsystem)
    const dialogRef = this.dialog.open(SystemInfoPopupComponent, {
      maxWidth: '1000px',
      minWidth: '350px',
      maxHeight: '900px',
      width: '90%',
      data: { idsystem: this.ticketInfo?.idsystem }
    });
  }

  getAllData() {
    this.connectServerService.getRequest(Connect.urlServerLaraApi, 'ticket/infoTicket', { idticket: this.idticket })
      .subscribe((val: ApiResponse<any>) => {
        if (val.data) {
          this.ticketInfo = val.data.infoTicket;
          this.messagesList = val.data.lines;
        }
      })
  }

  getSanitizedDescription(description: string): string | SafeHtml {
    return this.sanitizer.bypassSecurityTrustHtml(description || '');
  }

  getMessages() {
    this.connectServerService.getRequest(Connect.urlServerLaraApi, 'ticket/ticketLines', { idticket: this.idticket })
      .subscribe((val: ApiResponse<{ lines: TicketMessage[] }>) => {
        if (val.data) {
          this.messagesList = val.data.lines;
        }
      })
  }

  onFileSelected(event: Event): void {
    const input = event.target as HTMLInputElement;

    if (input.files) {
      const files = Array.from(input.files);

      // Verifica il numero massimo di file
      if (this.fileList.length + files.length > this.maxFiles) {
        alert(`Puoi caricare al massimo ${this.maxFiles} file.`);
        return;
      }

      files.forEach((file) => {
        // Verifica la dimensione massima del file
        if (file.size > this.maxFileSize) {
          alert(`Il file ${file.name} supera il limite di 5 MB.`);
        } else {
          this.fileList.push(file);

          // Creare un'anteprima dell'immagine
          const reader = new FileReader();
          reader.onload = (e: any) => {
            const fileExtension = file.name.split('.').pop() || '';
            const image: Image = {
              id: 0, // Puoi aggiornare l'id successivamente se necessario
              ext: fileExtension,
              src: e.target.result,
              title: file.name,
              folder: '',
            };
            this.imagesList.push(image);
          };
          reader.readAsDataURL(file);
        }
      });
    }
  }

  deleteFile(index: number): void {
    this.imagesList.splice(index, 1);
    this.fileList.splice(index, 1);
  }
}
